import useUserVarStore from "@app/store/useUserVarStore";
import DefaultImage from "@assets/tm_default_image.webp";
import ReactionToolbar from "@blocks/ReactionToolbar/ReactionToolbar";
import ShareSocialButton from "@blocks/ShareSocial/ShareSocialButton";
import QnParagraph from "@components/Layouts/QuickNews/QnParagraph";
import {
  QN_ANALYTICS_EVENT_BASE,
  QN_SOCIAL_ICON_LIST,
} from "@config/QuickNewsConstants";
import GetReadingTime from "@elements/ArticleParagraph/GetReadingTime";
import Image from "@elements/ResponsiveImage/Image";
import { capitalizeFirstCharOnly } from "@helper/stringTransform";
import { formatDateAgo } from "@helper/useDateFormat";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useArticleReactions from "@hooks/useArticleReactions";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

const SIZE_THRESHOLD_SMALL = 50;
const SIZE_THRESHOLD_MEDIUM = 100;

const ADDITION_SMALL = 30;
const ADDITION_MEDIUM = 20;
const ADDITION_LARGE = 10;

export default function QnArticleCard({
  item,
  typeIndex,
}: {
  item: ProcessedArticleData;
  typeIndex: number;
}) {
  const { userToken } = useUserVarStore();

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: item.id,
    userToken,
    publishDate: item.datetime,
    uniqueSectionName: item.sectionName,
  });

  const TmCustomEvent = useTMCustomEventAnalyticsCall();
  const readingTime = GetReadingTime(item.articleBody, item.urlPath);

  const [isDescriptionOpen, setDescriptionIsOpen] = useState(false);
  const [reduceSize, setReduceSize] = useState(100);

  // Ref to image element (safer than querySelector)
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleOnReadMoreClick = () => {
    TmCustomEvent({
      ...QN_ANALYTICS_EVENT_BASE,
      eventAction: `read full article::${typeIndex + 1}`,
      eventLabel: item.title,
    });
  };

  // Toggle article description
  const toggleDescription = () => {
    setDescriptionIsOpen((prev) => !prev);
  };

  function getAddition(reduceSize: number): number {
    if (reduceSize < SIZE_THRESHOLD_SMALL) return ADDITION_SMALL;
    if (reduceSize < SIZE_THRESHOLD_MEDIUM) return ADDITION_MEDIUM;
    return ADDITION_LARGE;
  }

  // Smooth height adjustment for article image
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    if (isDescriptionOpen) {
      const addition = getAddition(reduceSize);
      el.style.height = `${el.offsetHeight - (reduceSize + addition)}px`;
    } else {
      el.style.removeProperty("height");
    }
  }, [isDescriptionOpen, reduceSize, item.id]);

  return (
    <div
      data-testid="qn-article-card"
      className="relative grid h-full w-full bg-white-100 pb-5"
      style={{ gridTemplateRows: "auto 1fr" }}
    >
      {/* Article Image */}
      <div className="relative">
        <Image
          ref={imgRef}
          srcWidth={450}
          srcHeight={isDescriptionOpen ? 108 : 300}
          displayWidth={450}
          src={item.imageField?.url ?? DefaultImage}
          alt={item.imageAlt ?? item.title}
          className={twMerge(
            "qn-article-img w-full h-[270px] md:h-[280px] xxs:h-[230px] duration-500 pointer-events-none",
          )}
        />
      </div>

      {/* Content Block */}
      <div className="px-4 pt-2.5 text-sm text-grey-700 md:text-base">
        <h1 className="qn-article-title line-clamp-3 text-sm font-semibold leading-[150%] text-gray-100 md:text-2.5md lg:leading-[120%]">
          {item.title}
        </h1>

        <div className="py-2.5 text-xs font-semibold">
          <span>{capitalizeFirstCharOnly(item.sectionName || "")}</span>
          <span className="float-right lowercase">
            {formatDateAgo(item.dateEdited)}
          </span>
        </div>

        <div className="text-grey-700">
          <QnParagraph
            articleBody={item.articleBody}
            toggleDescription={toggleDescription}
            isDescriptionOpen={isDescriptionOpen}
            setReduceSize={setReduceSize}
          />
        </div>
      </div>

      {/* Action Bar */}
      <div className="actions absolute bottom-0 z-30 flex w-full items-center justify-between gap-2 bg-white-100 px-4 py-4">
        <Link
          reloadDocument
          to={item.linkTo}
          target="_blank"
          onClick={handleOnReadMoreClick}
          className="flex h-xl w-[80%] flex-[1_0_0] items-center justify-center gap-2 rounded-150 border border-gray-1100 text-center text-3xs font-semibold leading-[120%] text-red-100"
        >
          Read full article – {readingTime} mins read
        </Link>

        <ReactionToolbar
          articleId={item.id}
          showReactions={showReactions}
          reactions={reactions}
          userReactions={userReactions}
          reactionsError={reactionsError}
          handleReactionClick={handleReactionClick}
          expandStyle="-top-24 -right-12"
          qnStyle={true}
        />

        {/* Divider only when reactions are visible */}
        {showReactions && !reactionsError && (
          <div className="hidden h-[32px] w-[1px] lg:block" />
        )}

        <ShareSocialButton
          socialIconList={QN_SOCIAL_ICON_LIST}
          pagePath={`/quick-news/${item.id}`}
          mbPopupClassName="absolute right-0"
          quickNews={true}
          onClickGaEvent={() => {
            TmCustomEvent({
              ...QN_ANALYTICS_EVENT_BASE,
              eventAction: `share::${typeIndex + 1}`,
              eventLabel: item.title,
            });
          }}
        />
      </div>
    </div>
  );
}
